import React, { useState, useEffect, useCallback } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { useAuth } from '../context/AuthContext';
import CreditPurchaseHistory from './CreditPurchaseHistory';
import '../css/AdminDashboard.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Utility functions (shared between components)
const formatNumber = (num) => {
  return num ? num.toLocaleString() : '0';
};

function AdminDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [refreshInterval, setRefreshInterval] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      const response = await fetch('/api/auth/admin/dashboard-consolidated', {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch admin dashboard data');
      }
      
      const dashboardData = await response.json();
      
      // Safely update data to prevent crashes
      setData(prevData => {
        // If this is the first load, just set the data
        if (!prevData) return dashboardData;
        
        // Otherwise, merge carefully to prevent crashes during updates
        return {
          ...dashboardData,
          // Preserve any UI state that might be needed
        };
      });
      setError(null);
    } catch (err) {
      console.error('Error fetching admin data:', err);
      setError(err.message);
      // Don't clear existing data on error to prevent crashes
    } finally {
      setLoading(false);
    }
  }, [user.token]);

  useEffect(() => {
    fetchDashboardData();

    // Set up auto-refresh every 30 seconds
    const interval = setInterval(fetchDashboardData, 30000);
    setRefreshInterval(interval);

    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Never';
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  const getChartColors = () => {
    const isDarkMode = document.documentElement.classList.contains('dark-mode');
    
    if (isDarkMode) {
      return {
        successful: {
          border: 'rgb(76, 201, 240)', // primary-light in dark mode
          background: 'rgba(76, 201, 240, 0.1)',
        },
        failed: {
          border: 'rgb(248, 113, 113)', // error-color in dark mode
          background: 'rgba(248, 113, 113, 0.1)',
        },
      };
    } else {
      return {
        successful: {
          border: 'rgb(67, 97, 238)', // primary-color
          background: 'rgba(67, 97, 238, 0.1)',
        },
        failed: {
          border: 'rgb(244, 67, 54)', // error-color
          background: 'rgba(244, 67, 54, 0.1)',
        },
      };
    }
  };

  const createChartData = (chartData) => {
    if (!chartData || chartData.length === 0) return null;

    const colors = getChartColors();
    
    // Sort data by date and prepare for chart
    const sortedData = [...chartData].sort((a, b) => new Date(a.date) - new Date(b.date));
    
    const labels = sortedData.map(item => 
      new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    );
    
    const successfulData = sortedData.map(item => item.successful_count || 0);
    const failedData = sortedData.map(item => item.failed_count || 0);

    return {
      labels,
      datasets: [
        {
          label: 'Successful Lookups',
          data: successfulData,
          borderColor: colors.successful.border,
          backgroundColor: colors.successful.background,
          tension: 0.1,
          fill: false,
          pointBackgroundColor: colors.successful.border,
          pointBorderColor: colors.successful.border,
          pointHoverBackgroundColor: colors.successful.border,
          pointHoverBorderColor: '#fff',
          pointHoverBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
        {
          label: 'Failed Lookups',
          data: failedData,
          borderColor: colors.failed.border,
          backgroundColor: colors.failed.background,
          tension: 0.1,
          fill: false,
          pointBackgroundColor: colors.failed.border,
          pointBorderColor: colors.failed.border,
          pointHoverBackgroundColor: colors.failed.border,
          pointHoverBorderColor: '#fff',
          pointHoverBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 14,
            weight: '500',
          },
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(67, 97, 238, 0.3)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          title: function(context) {
            return `Date: ${context[0].label}`;
          },
          label: function(context) {
            return `${context.dataset.label}: ${formatNumber(context.parsed.y)}`;
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 12,
          },
        },
      },
      y: {
        display: true,
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          font: {
            size: 12,
          },
          callback: function(value) {
            return formatNumber(value);
          },
        },
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
  };

  const filterAndSortUsers = (users) => {
    if (!users) return [];
    
    let filtered = users.filter(user => 
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return filtered.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      
      if (sortBy === 'createdAt' || sortBy === 'lastActivity') {
        aVal = new Date(aVal || 0);
        bVal = new Date(bVal || 0);
      }
      
      if (sortOrder === 'desc') {
        return bVal > aVal ? 1 : -1;
      } else {
        return aVal > bVal ? 1 : -1;
      }
    });
  };

  if (loading && !data) {
    return (
      <div className="dashboard-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="dashboard-container">
        <div className="alert alert-error">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <span>{error}</span>
          <button onClick={fetchDashboardData} className="btn btn-primary btn-sm" style={{marginLeft: '1rem'}}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { systemStats, recentActivity, userOverview, creditDistribution,
          failureAnalysis, topPerformers, hubspotStats,
          recentSignups, creditAlerts, chartData, userEngagement,
          timeBasedAnalytics, creditConsumptionTrends, portalAnalytics,
          performanceMetrics } = data;

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Admin Dashboard</h1>
        <p className="welcome-message">
          Welcome back, <span className="user-email">{user.email}</span>!
          {refreshInterval && <span className="auto-refresh-indicator">• Auto-refreshing</span>}
        </p>
      </div>

      {error && (
        <div className="alert alert-error">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <span>{error}</span>
        </div>
      )}

      <div className="admin-tabs">
        <button 
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab-btn ${activeTab === 'activity' ? 'active' : ''}`}
          onClick={() => setActiveTab('activity')}
        >
          Live Activity
        </button>
        <button 
          className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Users
        </button>
        <button 
          className={`tab-btn ${activeTab === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          Analytics
        </button>
        <button
          className={`tab-btn ${activeTab === 'configuration' ? 'active' : ''}`}
          onClick={() => setActiveTab('configuration')}
        >
          Configuration
        </button>
        <button
          className={`tab-btn ${activeTab === 'purchases' ? 'active' : ''}`}
          onClick={() => setActiveTab('purchases')}
        >
          Credit Purchases
        </button>
        <button
          className={`tab-btn ${activeTab === 'logs' ? 'active' : ''}`}
          onClick={() => setActiveTab('logs')}
        >
          Logs
        </button>
      </div>

      {activeTab === 'overview' && (
        <div className="tab-content">
          {/* System Stats Grid */}
          <div className="dashboard-grid">
            <div className="dashboard-card">
              <div className="card-header">
                <h2>Total Users</h2>
                <button onClick={fetchDashboardData} className="btn-icon" title="Refresh Data">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 4v6h-6"></path>
                    <path d="M1 20v-6h6"></path>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"></path>
                    <path d="M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                  </svg>
                </button>
              </div>
              <div className="metric-display">
                <div className="metric-value">{formatNumber(systemStats?.totalUsers || 0)}</div>
                <div className="metric-label">
                  +{systemStats?.newUsers7d || 0} this week
                </div>
              </div>
            </div>

            <div className="dashboard-card">
              <div className="card-header">
                <h2>Success Rate</h2>
              </div>
              <div className="metric-display">
                <div className="metric-value success">{systemStats?.successRate || 0}%</div>
                <div className="metric-label">
                  {formatNumber(systemStats?.successfulLookups || 0)} successful
                </div>
              </div>
            </div>

            <div className="dashboard-card">
              <div className="card-header">
                <h2>Active Users</h2>
              </div>
              <div className="metric-display">
                <div className="metric-value primary">{systemStats?.activeUsers24h || 0}</div>
                <div className="metric-label">
                  {systemStats?.activeUsers7d || 0} this week
                </div>
              </div>
            </div>

            <div className="dashboard-card">
              <div className="card-header">
                <h2>Total Credits</h2>
              </div>
              <div className="metric-display">
                <div className="metric-value">{formatNumber(systemStats?.totalCreditsRemaining || 0)}</div>
                <div className="metric-label">
                  Avg: {Math.round(systemStats?.avgCreditsPerUser || 0)} per user
                </div>
              </div>
            </div>
          </div>

          {/* Usage Chart */}
          {chartData && chartData.length > 0 && (
            <div className="dashboard-card chart-card">
              <div className="card-header">
                <h2>Usage Trends (Last 30 Days)</h2>
              </div>
              <div className="chart-container">
                <Line data={createChartData(chartData)} options={chartOptions} />
              </div>
            </div>
          )}

          {/* Additional Stats Grid */}
          <div className="dashboard-grid">
            <div className="dashboard-card">
              <div className="card-header">
                <h2>Credit Distribution</h2>
              </div>
              <div className="distribution-list">
                {creditDistribution && creditDistribution.map((range, index) => (
                  <div key={index} className="distribution-item">
                    <div className="distribution-label">{range.credit_range}</div>
                    <div className="distribution-bar">
                      <div 
                        className="distribution-fill"
                        style={{ 
                          width: `${(range.user_count / Math.max(...creditDistribution.map(r => r.user_count))) * 100}%`
                        }}
                      ></div>
                      <span className="distribution-count">{range.user_count} users</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="dashboard-card">
              <div className="card-header">
                <h2>Performance Metrics</h2>
              </div>
              {performanceMetrics ? (
                <div className="integration-stats">
                  <div className="integration-item">
                    <div className="integration-icon">⚡</div>
                    <div className="integration-details">
                      <div className="integration-value">{performanceMetrics.requests_last_hour || 0}</div>
                      <div className="integration-label">Requests Last Hour</div>
                    </div>
                  </div>
                  <div className="integration-item">
                    <div className="integration-icon">📊</div>
                    <div className="integration-details">
                      <div className="integration-value">{Math.round(performanceMetrics.avg_requests_per_hour || 0)}</div>
                      <div className="integration-label">Avg/Hour (7d)</div>
                    </div>
                  </div>
                  <div className="integration-item">
                    <div className="integration-icon">🔥</div>
                    <div className="integration-details">
                      <div className="integration-value">{performanceMetrics.peak_requests_per_hour || 0}</div>
                      <div className="integration-label">Peak Hour (7d)</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="integration-stats">
                  <div className="integration-item">
                    <div className="integration-icon">🔗</div>
                    <div className="integration-details">
                      <div className="integration-value">{hubspotStats?.connected_users || 0}</div>
                      <div className="integration-label">Connected Users</div>
                    </div>
                  </div>
                  <div className="integration-item">
                    <div className="integration-icon">⚙️</div>
                    <div className="integration-details">
                      <div className="integration-value">{hubspotStats?.configured_portals || 0}</div>
                      <div className="integration-label">Configured Portals</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="dashboard-card">
              <div className="card-header">
                <h2>User Engagement</h2>
              </div>
              <div className="distribution-list">
                {userEngagement && userEngagement.map((level, index) => (
                  <div key={index} className="distribution-item">
                    <div className="distribution-label">
                      {level.engagement_level} ({level.user_count} users)
                    </div>
                    <div className="distribution-bar">
                      <div 
                        className="distribution-fill"
                        style={{ 
                          width: `${(level.user_count / Math.max(...userEngagement.map(e => e.user_count))) * 100}%`,
                          backgroundColor: level.engagement_level.includes('Active') ? '#4CAF50' : 
                                          level.engagement_level.includes('Recent') ? '#FF9800' : 
                                          level.engagement_level.includes('Dormant') ? '#F44336' : '#9E9E9E'
                        }}
                      ></div>
                      <span className="distribution-count">
                        Avg: {Math.round(level.avg_lookups || 0)} lookups
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'activity' && (
        <div className="tab-content">
          <div className="dashboard-card">
            <div className="card-header">
              <h2>Real-Time Activity Feed</h2>
              <div className="activity-stats">
                <span className="activity-stat success">
                  {recentActivity ? recentActivity.filter(a => a.successful).length : 0} successful
                </span>
                <span className="activity-stat error">
                  {recentActivity ? recentActivity.filter(a => !a.successful).length : 0} failed
                </span>
              </div>
            </div>
            <div className="activity-feed">
              {recentActivity && recentActivity.slice(0, 50).map((activity, index) => (
                <div key={activity.id || index} className={`activity-item ${activity.successful ? 'success' : 'error'}`}>
                  <div className="activity-icon">
                    {activity.successful ? (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="15" y1="9" x2="9" y2="15"></line>
                        <line x1="9" y1="9" x2="15" y2="15"></line>
                      </svg>
                    )}
                  </div>
                  <div className="activity-content">
                    <div className="activity-main">
                      <span className="activity-user">{activity.email}</span>
                      <span className="activity-action">
                        {activity.successful ? 'looked up' : 'failed to lookup'}
                      </span>
                      <span className="activity-zipcode">{activity.zipcode}</span>
                    </div>
                    <div className="activity-meta">
                      <span className="activity-time">{formatTimeAgo(activity.timestamp)}</span>
                      {!activity.successful && activity.reason && (
                        <span className="activity-reason">• {activity.reason.replace(/_/g, ' ')}</span>
                      )}
                      <span className="activity-credits">• {formatNumber(activity.userCredits)} credits remaining</span>
                    </div>
                  </div>
                </div>
              ))}
              {(!recentActivity || recentActivity.length === 0) && (
                <div className="empty-state">
                  <div className="empty-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 11H5a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h4"></path>
                      <path d="M9 7h10a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9"></path>
                      <path d="M3 19l6-6"></path>
                      <path d="M3 13l6-6"></path>
                    </svg>
                  </div>
                  <p>No recent activity to display</p>
                  <p className="empty-hint">Activity will appear here as users perform lookups</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="tab-content">
          <div className="dashboard-card">
            <div className="card-header">
              <h2>User Management</h2>
              <div className="user-controls">
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="sort-select"
                >
                  <option value="createdAt">Created Date</option>
                  <option value="credits">Credits</option>
                  <option value="totalLookups">Total Lookups</option>
                  <option value="successRate">Success Rate</option>
                  <option value="lastActivity">Last Activity</option>
                </select>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="sort-select"
                >
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </select>
              </div>
            </div>
            <div className="table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Credits</th>
                    <th>Activity</th>
                    <th>Success Rate</th>
                    <th>Last Seen</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filterAndSortUsers(userOverview).map((user) => (
                    <tr key={user.id}>
                      <td>
                        <div className="user-info">
                          <div className="user-email">{user.email}</div>
                          <div className="user-meta">
                            {user.referralType && (
                              <span className="user-tag">{user.referralType}</span>
                            )}
                            <span className="user-joined">
                              Joined {formatDate(user.createdAt)}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="credits-info">
                          <div className="credits-value">{formatNumber(user.credits)}</div>
                          {user.creditThreshold && (
                            <div className="credits-threshold">Threshold: {user.creditThreshold}</div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="activity-summary">
                          <div className="activity-total">{formatNumber(user.totalLookups)} total</div>
                          <div className="activity-recent">
                            {user.lookups24h} today • {user.lookups7d} this week
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="success-rate">
                          <div className="success-rate-bar">
                            <div 
                              className="success-rate-fill"
                              style={{ width: `${user.successRate}%` }}
                            ></div>
                          </div>
                          <span className="success-rate-text">{user.successRate}%</span>
                        </div>
                      </td>
                      <td>{formatTimeAgo(user.lastActivity)}</td>
                      <td>
                        <span className={`status-badge ${
                          user.lookups24h > 0 ? 'active' : 
                          user.credits === 0 ? 'no-credits' :
                          user.creditThreshold && user.credits < user.creditThreshold ? 'low-credits' :
                          'inactive'
                        }`}>
                          {user.lookups24h > 0 ? 'Active' :
                           user.credits === 0 ? 'No Credits' :
                           user.creditThreshold && user.credits < user.creditThreshold ? 'Low Credits' :
                           'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="tab-content">
          <div className="dashboard-grid">
            <div className="dashboard-card">
              <div className="card-header">
                <h2>Top Performers</h2>
              </div>
              <div className="performers-list">
                {topPerformers && topPerformers.slice(0, 10).map((performer, index) => (
                  <div key={index} className="performer-item">
                    <div className="performer-rank">#{index + 1}</div>
                    <div className="performer-info">
                      <div className="performer-email">{performer.email}</div>
                      <div className="performer-stats">
                        {formatNumber(performer.successful_all_time)} total • 
                        {performer.successful_7d} this week
                      </div>
                    </div>
                    <div className="performer-credits">{formatNumber(performer.credits)} credits</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="dashboard-card">
              <div className="card-header">
                <h2>Failure Analysis</h2>
              </div>
              <div className="failures-list">
                {failureAnalysis && failureAnalysis.map((failure, index) => (
                  <div key={index} className="failure-item">
                    <div className="failure-reason">{failure.reason.replace(/_/g, ' ')}</div>
                    <div className="failure-stats">
                      <div className="failure-count">{formatNumber(failure.count)} total</div>
                      <div className="failure-users">{failure.affected_users} users affected</div>
                      <div className="failure-recent">
                        {failure.count_24h} today • {failure.count_7d} this week
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="dashboard-card">
              <div className="card-header">
                <h2>Portal Analytics</h2>
              </div>
              <div className="signups-list">
                {portalAnalytics && portalAnalytics.slice(0, 10).map((portal, index) => (
                  <div key={index} className="signup-item">
                    <div className="signup-info">
                      <div className="signup-email">Portal {portal.portal_id}</div>
                      <div className="signup-meta">
                        {portal.unique_users} users • Last active: {formatTimeAgo(portal.last_activity)}
                      </div>
                    </div>
                    <div className="signup-stats">
                      <div className="signup-credits">{formatNumber(portal.total_lookups)} lookups</div>
                      <div className="signup-activity">
                        {portal.success_rate}% success rate
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="dashboard-card">
              <div className="card-header">
                <h2>Credit Consumption Trends</h2>
              </div>
              <div className="signups-list">
                {creditConsumptionTrends && creditConsumptionTrends.slice(0, 7).map((trend, index) => (
                  <div key={index} className="signup-item">
                    <div className="signup-info">
                      <div className="signup-email">{new Date(trend.date).toLocaleDateString()}</div>
                      <div className="signup-meta">
                        {trend.active_users} active users
                      </div>
                    </div>
                    <div className="signup-stats">
                      <div className="signup-credits">{trend.credits_consumed} credits used</div>
                      <div className="signup-activity">
                        {trend.avg_credits_per_user} avg/user • {trend.success_rate}% success
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="dashboard-card">
              <div className="card-header">
                <h2>Hourly Usage Patterns</h2>
              </div>
              <div className="distribution-list">
                {timeBasedAnalytics && timeBasedAnalytics.slice(0, 12).map((hour, index) => {
                  const maxLookups = Math.max(...timeBasedAnalytics.map(h => h.total_lookups));
                  return (
                    <div key={index} className="distribution-item">
                      <div className="distribution-label">
                        {hour.hour}:00 ({formatNumber(hour.total_lookups)} lookups)
                      </div>
                      <div className="distribution-bar">
                        <div 
                          className="distribution-fill"
                          style={{ 
                            width: `${(hour.total_lookups / maxLookups) * 100}%`,
                            backgroundColor: hour.success_rate >= 90 ? '#4CAF50' : 
                                            hour.success_rate >= 70 ? '#FF9800' : '#F44336'
                          }}
                        ></div>
                        <span className="distribution-count">{hour.success_rate}% success</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="dashboard-card alert-card">
              <div className="card-header">
                <h2>Credit Alerts</h2>
              </div>
              {creditAlerts && creditAlerts.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                  </div>
                  <p>No users below credit threshold</p>
                  <p className="empty-hint">All users have sufficient credits</p>
                </div>
              ) : (
                <div className="alerts-list">
                  {creditAlerts && creditAlerts.map((alert, index) => (
                    <div key={index} className="alert-item">
                      <div className="alert-info">
                        <div className="alert-email">{alert.email}</div>
                        <div className="alert-credits">
                          {alert.credits} / {alert.credit_threshold} credits
                        </div>
                      </div>
                      <div className="alert-severity">
                        <div className="credits-deficit">-{alert.credits_below_threshold}</div>
                        <div className="alert-activity">{alert.recent_lookups} recent lookups</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Recent Signups moved to separate section */}
          <div className="dashboard-card">
            <div className="card-header">
              <h2>Recent Signups & Onboarding</h2>
            </div>
            <div className="signups-list">
              {recentSignups && recentSignups.slice(0, 15).map((signup, index) => (
                <div key={index} className="signup-item">
                  <div className="signup-info">
                    <div className="signup-email">{signup.email}</div>
                    <div className="signup-meta">
                      {formatDate(signup.created_at)}
                      {signup.referral_type && <span> • via {signup.referral_type}</span>}
                    </div>
                  </div>
                  <div className="signup-stats">
                    <div className="signup-credits">{signup.credits} credits</div>
                    <div className="signup-activity">
                      {signup.lookups_since_signup} lookups
                      {signup.last_lookup && (
                        <span> • Last: {formatTimeAgo(signup.last_lookup)}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'configuration' && (
        <div className="tab-content">
          <div className="dashboard-grid">
            {/* Property Configuration Overview */}
            <div className="dashboard-card">
              <div className="card-header">
                <h2>Property Configuration Overview</h2>
              </div>
              {data.propertyConfigurationReport ? (
                <div className="integration-stats">
                  <div className="integration-item">
                    <div className="integration-icon">❌</div>
                    <div className="integration-details">
                      <div className="integration-value">{data.propertyConfigurationReport.users_no_config || 0}</div>
                      <div className="integration-label">No Properties Configured</div>
                    </div>
                  </div>
                  <div className="integration-item">
                    <div className="integration-icon">📧</div>
                    <div className="integration-details">
                      <div className="integration-value">{data.propertyConfigurationReport.users_contact_only || 0}</div>
                      <div className="integration-label">Contact Properties Only</div>
                    </div>
                  </div>
                  <div className="integration-item">
                    <div className="integration-icon">🏢</div>
                    <div className="integration-details">
                      <div className="integration-value">{data.propertyConfigurationReport.users_company_only || 0}</div>
                      <div className="integration-label">Company Properties Only</div>
                    </div>
                  </div>
                  <div className="integration-item">
                    <div className="integration-icon">✅</div>
                    <div className="integration-details">
                      <div className="integration-value">{data.propertyConfigurationReport.users_fully_configured || 0}</div>
                      <div className="integration-label">Fully Configured</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="empty-state">
                  <p>Loading configuration data...</p>
                </div>
              )}
            </div>

            {/* Connection Status */}
            <div className="dashboard-card">
              <div className="card-header">
                <h2>Connection Status</h2>
              </div>
              {data.propertyConfigurationReport ? (
                <div className="integration-stats">
                  <div className="integration-item">
                    <div className="integration-icon">🔗</div>
                    <div className="integration-details">
                      <div className="integration-value">{data.propertyConfigurationReport.total_users_with_connections || 0}</div>
                      <div className="integration-label">Total Users with Connections</div>
                    </div>
                  </div>
                  <div className="integration-item">
                    <div className="integration-icon">🚫</div>
                    <div className="integration-details">
                      <div className="integration-value">{data.propertyConfigurationReport.users_not_connected || 0}</div>
                      <div className="integration-label">Users Not Connected</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="empty-state">
                  <p>Loading connection data...</p>
                </div>
              )}
            </div>
          </div>

          {/* Detailed User Configuration Report */}
          <div className="dashboard-card">
            <div className="card-header">
              <h2>User Configuration Details</h2>
              <div className="user-controls">
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
            </div>
            <div className="table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Contact Config</th>
                    <th>Company Config</th>
                    <th>International Lookup</th>
                    <th>Status</th>
                    <th>Connected Portals</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {data.propertyConfigurationReport?.user_details && 
                   data.propertyConfigurationReport.user_details
                     .filter(user => user.email.toLowerCase().includes(searchTerm.toLowerCase()))
                     .map((user, index) => (
                    <tr key={index}>
                      <td>
                        <div className="user-info">
                          <div className="user-email">{user.email}</div>
                        </div>
                      </td>
                      <td>
                        <span className={`status-badge ${user.has_contact_config ? 'active' : 'inactive'}`}>
                          {user.has_contact_config ? 'Configured' : 'Not Configured'}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${user.has_company_config ? 'active' : 'inactive'}`}>
                          {user.has_company_config ? 'Configured' : 'Not Configured'}
                        </span>
                      </td>
                      <td>
                        {user.international_lookup_portals?.length ? user.international_lookup_portals.map(portal => (
                          <div key={portal.portal_id}>
                            <span className={`status-badge ${portal.enabled ? 'active' : 'inactive'}`}>
                              {portal.enabled ? 'On' : 'Off'}
                            </span>{' '}
                            <span>Portal {portal.portal_id}</span>
                          </div>
                        )) : 'No connected portals'}
                      </td>
                      <td>
                        <span className={`status-badge ${
                          user.config_status === 'fully_configured' ? 'active' :
                          user.config_status === 'contact_only' || user.config_status === 'company_only' ? 'low-credits' :
                          'no-credits'
                        }`}>
                          {user.config_status === 'fully_configured' ? 'Fully Configured' :
                           user.config_status === 'contact_only' ? 'Contact Only' :
                           user.config_status === 'company_only' ? 'Company Only' :
                           'No Configuration'}
                        </span>
                      </td>
                      <td>{user.connected_portals}</td>
                      <td>{formatDate(user.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(!data.propertyConfigurationReport?.user_details || data.propertyConfigurationReport.user_details.length === 0) && (
                <div className="empty-state">
                  <div className="empty-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 11H5a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h4"></path>
                      <path d="M9 7h10a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9"></path>
                      <path d="M3 19l6-6"></path>
                      <path d="M3 13l6-6"></path>
                    </svg>
                  </div>
                  <p>No configuration data available</p>
                  <p className="empty-hint">Configuration details will appear here when users connect and configure their HubSpot properties</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'logs' && (
        <LogsViewer user={user} />
      )}

      {activeTab === 'purchases' && (
        <CreditPurchaseHistory user={user} />
      )}
    </div>
  );
}

// Logs Viewer Component
function LogsViewer({ user }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    level: 'all',
    search: '',
    startDate: '',
    endDate: '',
    userId: '',
    requestId: '',
    sourceFile: '',
    lookupOnly: false,
    limit: 50,
    offset: 0
  });
  const [pagination, setPagination] = useState(null);
  const [stats, setStats] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const [autoRefreshSeconds, setAutoRefreshSeconds] = useState(30);
  const [timeline, setTimeline] = useState({ open: false, requestId: null, items: [], loading: false, error: null });
  const [expandedMetadata, setExpandedMetadata] = useState({});
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
  const [groupLogs, setGroupLogs] = useState(true); // Group logs by request ID
  const [statsPeriod, setStatsPeriod] = useState('24 hours'); // Current stats period

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();

      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          queryParams.append(key, value);
        }
      });

      // Add cache-busting timestamp to ensure fresh data
      queryParams.append('_t', Date.now());
      
      const response = await fetch(`/api/logs/admin/logs?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch logs');
      }

      const data = await response.json();
      setLogs(data.logs || []);
      setPagination(data.pagination);
      setStats(prevStats => ({ ...prevStats, ...data.stats }));
      setError(null);
    } catch (err) {
      console.error('Error fetching logs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, user.token]);

  const fetchLogStats = useCallback(async (period = '24 hours') => {
    try {
      // Add cache-busting timestamp to ensure fresh data
      const timestamp = Date.now();
      const response = await fetch(`/api/logs/admin/logs/stats?period=${encodeURIComponent(period)}&_t=${timestamp}`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch log stats');
      }

      const data = await response.json();
      setStats(prevStats => ({ ...prevStats, ...data }));
    } catch (err) {
      console.error('Error fetching log stats:', err);
    }
  }, [user.token]);

  useEffect(() => {
    const timer = setTimeout(fetchLogs, filters.search ? 250 : 0);
    return () => clearTimeout(timer);
  }, [fetchLogs, filters.search]);

  useEffect(() => {
    fetchLogStats(statsPeriod);
  }, [fetchLogStats, statsPeriod]);

  useEffect(() => {
    if (!autoRefreshEnabled) return undefined;
    const interval = setInterval(() => {
      fetchLogs();
      fetchLogStats(statsPeriod);
    }, Math.max(5, autoRefreshSeconds) * 1000);
    return () => clearInterval(interval);
  }, [autoRefreshEnabled, autoRefreshSeconds, fetchLogs, fetchLogStats, statsPeriod]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      offset: key === 'offset' ? value : 0 // Reset pagination when changing filters
    }));
  };

  const applyQuickRange = (range) => {
    const end = new Date();
    const start = new Date(end);
    if (range === '1h') start.setHours(end.getHours() - 1);
    if (range === '24h') start.setDate(end.getDate() - 1);
    if (range === '7d') start.setDate(end.getDate() - 7);
    const toDateStr = (d) => d.toISOString().slice(0, 10);
    handleFilterChange('startDate', toDateStr(start));
    handleFilterChange('endDate', toDateStr(end));
  };

  const formatLogDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getLogLevelColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'error': return '#ef4444';
      case 'warn': return '#f59e0b';
      case 'info': return '#3b82f6';
      case 'debug': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  const getStatusColor = (status) => {
    if (!status) return '#6b7280';
    if (status >= 500) return '#ef4444';
    if (status >= 400) return '#f59e0b';
    if (status >= 200) return '#10b981';
    return '#6b7280';
  };

  const isHubSpotLog = (log) => {
    const msg = (log.message || '').toLowerCase();
    const url = (log.metadata?.url || '').toLowerCase();
    const src = (log.sourceFile || '').toLowerCase();
    return msg.includes('hubspot') || url.includes('hubspot') || url.includes('/api/hubspot') || src.includes('hubspot');
  };

  const highlightMessage = (log) => {
    const status = log.metadata?.statusCode;
    const rt = log.metadata?.responseTime;
    const url = log.metadata?.url;
    const method = log.metadata?.method;
    const isHs = isHubSpotLog(log);
    return (
      <div className={`log-message-rich ${isHs ? 'hubspot-log' : ''}`}>
        {status ? (
          <span className="status-pill" style={{ borderColor: getStatusColor(status), color: getStatusColor(status) }}>
            {status}{rt ? ` · ${rt}ms` : ''}
          </span>
        ) : null}
        {method && url ? (
          <span className="route-pill">
            <strong>{method}</strong> {url}
          </span>
        ) : null}
        <span className="message-text">{log.message}</span>
      </div>
    );
  };

  const toggleMetadata = (id) => {
    setExpandedMetadata(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const openTimeline = async (requestId) => {
    if (!requestId) return;
    setTimeline({ open: true, requestId, items: [], loading: true, error: null });
    try {
      const res = await fetch(`/api/logs/admin/logs/request/${requestId}`, {
        headers: { 'Authorization': `Bearer ${user.token}`, 'Content-Type': 'application/json' }
      });
      if (!res.ok) throw new Error('Failed to fetch request timeline');
      const data = await res.json();
      setTimeline({ open: true, requestId, items: data.logs || [], loading: false, error: null });
    } catch (e) {
      setTimeline({ open: true, requestId, items: [], loading: false, error: e.message });
    }
  };

  const closeTimeline = () => setTimeline({ open: false, requestId: null, items: [], loading: false, error: null });

  // Group logs by request ID for consolidated view
  const groupedLogs = React.useMemo(() => {
    if (!groupLogs || !logs.length) return logs;
    
    const grouped = new Map();
    const ungrouped = [];
    
    logs.forEach(log => {
      if (log.requestId) {
        if (!grouped.has(log.requestId)) {
          grouped.set(log.requestId, []);
        }
        grouped.get(log.requestId).push(log);
      } else {
        ungrouped.push(log);
      }
    });
    
    // Convert grouped logs to request summaries
    const requestSummaries = Array.from(grouped.entries()).map(([requestId, requestLogs]) => {
      // Sort logs chronologically
      const sortedLogs = requestLogs.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      const firstLog = sortedLogs[0];
      const lastLog = sortedLogs[sortedLogs.length - 1];
      
      // Determine request status
      const hasErrors = sortedLogs.some(log => log.level === 'error');
      const hasWarnings = sortedLogs.some(log => log.level === 'warn');
      const status = hasErrors ? 'error' : hasWarnings ? 'warning' : 'success';
      
      // Authentication runs after the incoming-request event is logged, so use
      // any authenticated event in the request instead of assuming the first
      // event contains the user.
      const userLog = [...sortedLogs].reverse().find(log => log.userEmail || log.userId);
      const userEmail = userLog?.userEmail || userLog?.userId;
      const userId = userLog?.userId || null;
      // Extract credit information from individual logs
      const creditInfo = {};
      
      // Look for credit information in the logs (priority order)
      for (const log of sortedLogs) {
        if (log.metadata?.userCreditsAfter !== undefined) {
          creditInfo.userCreditsAfter = log.metadata.userCreditsAfter;
          break; // This is the final balance, use it
        }
      }
      
      // If no userCreditsAfter found, look for 'after' from credit deduction
      if (creditInfo.userCreditsAfter === undefined) {
        for (const log of sortedLogs) {
          if (log.metadata?.after !== undefined) {
            creditInfo.after = log.metadata.after;
            break;
          }
        }
      }
      
      // If no final balance found, look for before balance
      if (creditInfo.userCreditsAfter === undefined && creditInfo.after === undefined) {
        for (const log of sortedLogs) {
          if (log.metadata?.userCreditsBefore !== undefined) {
            creditInfo.userCreditsBefore = log.metadata.userCreditsBefore;
            break;
          }
        }
      }
      
      // Create request summary
      return {
        id: requestId,
        requestId,
        timestamp: firstLog.timestamp,
        userEmail,
        userId,
        level: status,
        message: `Request processed (${sortedLogs.length} steps)`,
        sourceFile: firstLog.sourceFile,
        requestLogs: sortedLogs,
        stepCount: sortedLogs.length,
        duration: new Date(lastLog.timestamp) - new Date(firstLog.timestamp),
        status,
        isGrouped: true,
        successful: !hasErrors,
        metadata: creditInfo // Include the extracted credit information
      };
    });
    
    // Combine request summaries with ungrouped logs and sort by timestamp
    const combined = [...requestSummaries, ...ungrouped].sort((a, b) => 
      new Date(b.timestamp) - new Date(a.timestamp)
    );
    
    return combined;
  }, [logs, groupLogs]);

  const openRequestTimeline = async (requestData) => {
    if (requestData.requestLogs) {
      // Use the grouped logs we already have
      setTimeline({ 
        open: true, 
        requestId: requestData.requestId, 
        items: requestData.requestLogs, 
        loading: false, 
        error: null 
      });
    } else {
      // Fallback to API call
      await openTimeline(requestData.requestId);
    }
  };

  const getLogLevelIcon = (level) => {
    switch (level?.toLowerCase()) {
      case 'error':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        );
      case 'warn':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        );
      case 'info':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
        );
      case 'debug':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 11H5a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h4"></path>
            <path d="M9 7h10a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9"></path>
            <path d="M3 19l6-6"></path>
            <path d="M3 13l6-6"></path>
          </svg>
        );
      default:
        return (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
          </svg>
        );
    }
  };

  return (
    <div className="tab-content">
      <div className="logs-top-section">
        {/* Enhanced Log Statistics */}
        <div className="dashboard-card log-stats-card">
          <div className="card-header stats-header">
            <h2>Log Statistics</h2>
            <div className="stats-controls">
              <select 
                className="period-selector"
                value={statsPeriod}
                onChange={(e) => setStatsPeriod(e.target.value)}
              >
                <option value="1 hour">Last Hour</option>
                <option value="24 hours">Last 24 Hours</option>
                <option value="7 days">Last 7 Days</option>
                <option value="30 days">Last 30 Days</option>
              </select>
              <button onClick={() => fetchLogStats(statsPeriod)} className="refresh-btn" title="Refresh Stats">
                Refresh
              </button>
            </div>
          </div>
          
          {/* Main Stats Grid */}
          <div className="enhanced-stats-grid">

            {/* Request Performance Stats */}
            <div className="stats-section">
              <h4 className="stats-section-title">Request Performance</h4>
              <div className="stats-row">
                <div className="stat-item centered">
                  <div className="stat-value success">
                    {stats?.requestStats?.successful_requests || 0}
                  </div>
                  <div className="stat-label">Successful Requests</div>
                  <div className="stat-subtext">
                    {stats?.requestStats?.success_rate || 0}% success rate
                  </div>
                </div>
                
                <div className="stat-item centered">
                  <div className="stat-value error">
                    {stats?.requestStats?.failed_requests || 0}
                  </div>
                  <div className="stat-label">Failed Requests</div>
                  <div className="stat-subtext">
                    {stats?.requestStats?.total_requests > 0 
                      ? Math.round(((stats?.requestStats?.failed_requests || 0) / stats?.requestStats?.total_requests) * 100)
                      : 0}% failure rate
                  </div>
                </div>

                <div className="stat-item centered">
                  <div className="stat-value info">
                    {stats?.performanceStats?.avg_response_time || 0}ms
                    {stats?.performanceStats?.has_sufficient_data === false && (
                      <span className="insufficient-data-indicator" title={`Only ${stats.performanceStats.data_span_hours}h of data available for ${stats.performanceStats.requested_period_hours}h period`}>*</span>
                    )}
                  </div>
                  <div className="stat-label">Avg Response Time</div>
                  <div className="stat-subtext">
                    {stats?.performanceStats?.has_sufficient_data === false 
                      ? `Limited data (~${stats?.performanceStats?.data_span_hours}h)`
                      : `${stats?.requestStats?.total_requests || 0} requests`
                    }
                  </div>
                </div>
              </div>
            </div>

            {/* Top Errors Section */}
            {stats?.topErrors && stats.topErrors.length > 0 && (
              <div className="stats-section">
                <h4 className="stats-section-title">Top Issues</h4>
                <div className="top-errors-list">
                  {stats.topErrors.slice(0, 3).map((error, index) => (
                    <div key={index} className="error-item">
                      <div className="error-indicator" style={{ backgroundColor: getLogLevelColor(error.level) }}></div>
                      <div className="error-content">
                        <div className="error-message">{error.message.substring(0, 60)}...</div>
                        <div className="error-meta">
                          <span className="error-count">{formatNumber(error.count)} occurrences</span>
                          <span className="error-users">{formatNumber(error.affected_users)} users</span>
                          <span className="error-level">{error.level.toUpperCase()}</span>
                        </div>
                </div>
              </div>
            ))}
          </div>
        </div>
            )}

            {/* Activity Overview */}
            <div className="stats-section">
              <h4 className="stats-section-title">Activity Overview</h4>
              <div className="stats-row">
                <div className="stat-item centered">
                  <div className="stat-value primary">
                    {formatNumber(stats?.requestStats?.total_requests || 0)}
                  </div>
                  <div className="stat-label">Total Lookups</div>
                  <div className="stat-subtext">
                    {stats?.period || 'Last 24 hours'}
                  </div>
                </div>

                <div className="stat-item centered">
                  <div className="stat-value warning">
                    {stats?.requestStats?.active_users || 0}
                  </div>
                  <div className="stat-label">Active Users</div>
                  <div className="stat-subtext">
                    With lookup activity
                  </div>
                </div>

                <div className="stat-item centered">
                  <div className="stat-value info">
                    {Number(stats?.logStats?.error_logs || 0) + Number(stats?.logStats?.warning_logs || 0)}
                  </div>
                  <div className="stat-label">Issues Found</div>
                  <div className="stat-subtext">
                    Errors & warnings
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Filters */}
        <div className="dashboard-card filters-card">
          <div className="card-header">
            <h2>Filters & Search</h2>
          </div>

          <div className="filters-content">
            {/* Primary Filters Row */}
            <div className="filters-primary">
              <div className="filter-group search-group">
                <div className="filter-input-wrapper">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="M21 21l-4.35-4.35"></path>
                  </svg>
              <input
                type="text"
                    placeholder="Search logs, messages, users..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="filter-input search-input"
                  />
                  {filters.search && (
                    <button
                      className="clear-search"
                      onClick={() => handleFilterChange('search', '')}
                      title="Clear search"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  )}
                </div>
            </div>

              <div className="filter-group level-group">
                <label>Log Level</label>
                <select
                  value={filters.level}
                  onChange={(e) => handleFilterChange('level', e.target.value)}
                  className="filter-select modern"
                >
                  <option value="all">All Levels</option>
                  <option value="error">Errors Only</option>
                  <option value="warn">Warnings Only</option>
                  <option value="info">Info Only</option>
                  <option value="debug">Debug Only</option>
                </select>
              </div>

              <div className="filter-group date-group">
                <label>Time Range</label>
                <div className="date-inputs">
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    className="filter-input date-input"
                    placeholder="Start date"
              />
                  <span className="date-separator">to</span>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    className="filter-input date-input"
                    placeholder="End date"
              />
                </div>
              </div>
            </div>

            {/* Quick Filter Chips */}
            <div className="quick-filters">
              <div className="filter-section">
                <span className="section-label">Quick Filters:</span>
                <div className="chip-group">
                  <button
                    className={`filter-chip ${filters.level === 'error' ? 'active' : ''}`}
                    onClick={() => handleFilterChange('level', filters.level === 'error' ? 'all' : 'error')}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    Errors
                  </button>
                  <button
                    className={`filter-chip ${filters.level === 'warn' ? 'active' : ''}`}
                    onClick={() => handleFilterChange('level', filters.level === 'warn' ? 'all' : 'warn')}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                      <line x1="12" y1="9" x2="12" y2="13"></line>
                      <line x1="12" y1="17" x2="12.01" y2="17"></line>
                    </svg>
                    Warnings
                  </button>
                </div>
              </div>

              <div className="filter-section">
                <span className="section-label">Time Range:</span>
                <div className="chip-group">
                  <button className="filter-chip time-chip" onClick={() => applyQuickRange('1h')}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    Last Hour
                  </button>
                  <button className="filter-chip time-chip" onClick={() => applyQuickRange('24h')}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    Last 24h
                  </button>
                  <button className="filter-chip time-chip" onClick={() => applyQuickRange('7d')}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    Last 7 Days
                  </button>
                </div>
              </div>

              <div className="filter-section">
                <span className="section-label">Log Scope:</span>
                <div className="auto-refresh-controls">
                  <label className="toggle modern">
                    <input
                      type="checkbox"
                      checked={filters.lookupOnly}
                      onChange={(e) => handleFilterChange('lookupOnly', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                    <span className="toggle-label">Lookups only</span>
                  </label>
                </div>
              </div>

              <div className="filter-section">
                <span className="section-label">Group Logs:</span>
                <div className="auto-refresh-controls">
                  <label className="toggle modern">
                    <input
                      type="checkbox"
                      checked={groupLogs}
                      onChange={(e) => setGroupLogs(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                    <span className="toggle-label">By Request</span>
                  </label>
                </div>
              </div>

              <div className="filter-section">
                <span className="section-label">Auto Refresh:</span>
                <div className="auto-refresh-controls">
                  <label className="toggle modern">
                    <input
                      type="checkbox"
                      checked={autoRefreshEnabled}
                      onChange={(e) => setAutoRefreshEnabled(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                    <span className="toggle-label">Enabled</span>
                  </label>
                  {autoRefreshEnabled && (
                    <div className="refresh-interval">
                      <input
                        type="number"
                        min="5"
                        max="300"
                        value={autoRefreshSeconds}
                        onChange={(e) => setAutoRefreshSeconds(parseInt(e.target.value || '30', 10))}
                        className="filter-input interval-input"
                      />
                      <span className="interval-label">seconds</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Advanced Filters (Collapsible) */}
            <details className="advanced-filters">
              <summary className="advanced-toggle">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 5v14"></path>
                  <path d="M5 12h14"></path>
                </svg>
                Advanced Filters
              </summary>
              <div className="advanced-filters-content">
                <div className="filters-grid advanced">
            <div className="filter-group">
                    <label>User ID/Email:</label>
              <input
                type="text"
                      placeholder="Search by user..."
                value={filters.userId}
                onChange={(e) => handleFilterChange('userId', e.target.value)}
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label>Request ID:</label>
              <input
                type="text"
                      placeholder="Specific request ID..."
                value={filters.requestId}
                onChange={(e) => handleFilterChange('requestId', e.target.value)}
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label>Source File:</label>
              <input
                type="text"
                      placeholder="e.g. hubspot.js, routes/auth.js"
                value={filters.sourceFile}
                onChange={(e) => handleFilterChange('sourceFile', e.target.value)}
                className="filter-input"
              />
            </div>

                  <div className="filter-group">
                    <label>Results per page:</label>
                    <select
                      value={filters.limit}
                      onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                      className="filter-select"
                    >
                      <option value="25">25 per page</option>
                      <option value="50">50 per page</option>
                      <option value="100">100 per page</option>
                      <option value="200">200 per page</option>
                    </select>
          </div>
                </div>
              </div>
            </details>
          </div>
        </div>
      </div>

      {/* Logs Display */}
      <div className="dashboard-card logs-card">
        <div className="card-header">
          <div className="header-left">
          <h2>Application Logs</h2>
            <div className="view-toggle">
              <button
                className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
                onClick={() => setViewMode('table')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 6h18"></path>
                  <path d="M3 12h18"></path>
                  <path d="M3 18h18"></path>
                </svg>
                Table
              </button>
              <button
                className={`view-btn ${viewMode === 'cards' ? 'active' : ''}`}
                onClick={() => setViewMode('cards')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7"></rect>
                  <rect x="14" y="3" width="7" height="7"></rect>
                  <rect x="14" y="14" width="7" height="7"></rect>
                  <rect x="3" y="14" width="7" height="7"></rect>
                </svg>
                Cards
              </button>
            </div>
          </div>
          <div className="card-actions">
            <button onClick={fetchLogs} className="btn btn-primary btn-sm" disabled={loading}>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 4v6h-6"></path>
                <path d="M1 20v-6h6"></path>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"></path>
                <path d="M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-error">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <span>{error}</span>
            <button onClick={fetchLogs} className="btn btn-primary btn-sm" style={{marginLeft: '1rem'}}>
              Retry
            </button>
          </div>
        )}

        {loading && logs.length === 0 ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading logs...</p>
          </div>
        ) : logs.length === 0 ? (
              <div className="empty-state">
            <div className="empty-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 11H5a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h4"></path>
                <path d="M9 7h10a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9"></path>
                <path d="M3 19l6-6"></path>
                <path d="M3 13l6-6"></path>
              </svg>
            </div>
                <p>No logs found</p>
            <p className="empty-hint">Try adjusting your filters or check back later</p>
          </div>
        ) : (
          <>
            {viewMode === 'table' ? (
          <div className="table-container">
                <table className="logs-table modern">
              <thead>
                <tr>
                    <th className="col-time">Time</th>
                    <th className="col-level">Level</th>
                    <th className="col-user">User</th>
                    <th className="col-message">Message</th>
                    <th className="col-request">Request ID</th>
                    <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                    {groupedLogs.map(log => (
                      <tr key={log.id || log.requestId} className={`log-row log-${log.level} ${log.isGrouped ? 'grouped-request' : ''} ${(log.isGrouped && log.requestLogs?.some(l => isHubSpotLog(l))) || (!log.isGrouped && isHubSpotLog(log)) ? 'hubspot-log' : ''}`}>
                    <td className="log-time">
                          <div className="time-primary">{new Date(log.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</div>
                          <div className="time-secondary">{new Date(log.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                    </td>
                    <td className="log-level">
                          <div className="level-badge" style={{
                            backgroundColor: `${getLogLevelColor(log.level)}20`,
                            borderColor: getLogLevelColor(log.level),
                            color: getLogLevelColor(log.level)
                          }}>
                        {getLogLevelIcon(log.level)}
                            <span className="level-text">{log.level.toUpperCase()}</span>
                          </div>
                          {log.isGrouped && (
                            <div className="step-count">{log.stepCount} steps</div>
                          )}
                    </td>
                    <td className="log-user">
                          <div className="user-info">
                            <span className="user-name">{log.userEmail || log.userId || 'System'}</span>
                            {log.userEmail && (
                              <span className="user-credits">
                                {log.metadata?.userCreditsAfter !== undefined 
                                  ? `${log.metadata.userCreditsAfter} credits`
                                  : log.metadata?.after !== undefined 
                                    ? `${log.metadata.after} credits`
                                    : log.metadata?.userCreditsBefore !== undefined
                                      ? `${log.metadata.userCreditsBefore} credits`
                                      : 'User'
                                }
                              </span>
                            )}
                          </div>
                    </td>
                    <td className="log-message">
                          <div className="message-content">
                            {log.isGrouped ? (
                              <div className="grouped-message">
                                <div className="request-summary">
                                  <span className="message-text">{log.message}</span>
                                  <span className="duration">{Math.round(log.duration || 0)}ms</span>
                                  <span className={`status-indicator status-${log.status}`}>{log.status}</span>
                                </div>
                              </div>
                            ) : (
                              <>
                      {highlightMessage(log)}
                              </>
                            )}
                          </div>
                          {!log.isGrouped && log.metadata && Object.keys(log.metadata).length > 0 && (
                            <div className="metadata-preview">
                              {Object.entries(log.metadata).slice(0, 2).map(([key, value]) => (
                            <span key={key} className="metadata-item">
                                  <strong>{key}:</strong> {typeof value === 'object' ? '[Object]' : String(value).substring(0, 20)}
                            </span>
                          ))}
                              {Object.keys(log.metadata).length > 2 && (
                                <span className="metadata-more">+{Object.keys(log.metadata).length - 2} more</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="log-request-id">
                          {log.requestId ? (
                            <code className="request-id" title={log.requestId}>{log.requestId.substring(0, 8)}...</code>
                          ) : (
                            <span className="no-request">-</span>
                      )}
                    </td>
                    <td className="log-actions">
                          <div className="action-buttons">
                            {log.isGrouped ? (
                        <button
                                onClick={() => openRequestTimeline(log)}
                                className="btn-icon btn-timeline"
                                title="View Request Timeline"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <circle cx="12" cy="12" r="10"></circle>
                                  <polyline points="12 6 12 12 16 14"></polyline>
                                </svg>
                        </button>
                            ) : (
                              <>
                      <button
                        onClick={() => setSelectedLog(log)}
                                  className="btn-icon btn-details"
                        title="View Details"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      </button>
                                {log.requestId && (
                                  <button
                                    onClick={() => openTimeline(log.requestId)}
                                    className="btn-icon btn-timeline"
                                    title="View Timeline"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <circle cx="12" cy="12" r="10"></circle>
                                      <polyline points="12 6 12 12 16 14"></polyline>
                                    </svg>
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
                </div>
            ) : (
              <div className="logs-cards">
                {groupedLogs.map(log => (
                  <div key={log.id || log.requestId} className={`log-card log-${log.level} ${log.isGrouped ? 'grouped-request' : ''} ${(log.isGrouped && log.requestLogs?.some(l => isHubSpotLog(l))) || (!log.isGrouped && isHubSpotLog(log)) ? 'hubspot-log' : ''}`}>
                    <div className="card-header">
                      <div className="card-time">
                        <div className="time-primary">{new Date(log.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</div>
                        <div className="time-secondary">{new Date(log.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
              </div>
                      <div className="card-level">
                        <div className="level-badge" style={{
                          backgroundColor: `${getLogLevelColor(log.level)}20`,
                          borderColor: getLogLevelColor(log.level),
                          color: getLogLevelColor(log.level)
                        }}>
                          {getLogLevelIcon(log.level)}
                          <span className="level-text">{log.level.toUpperCase()}</span>
                        </div>
                        {log.isGrouped && (
                          <div className="step-count-card">{log.stepCount} steps</div>
                        )}
                      </div>
                    </div>
                    <div className="card-body">
                      <div className="card-user">
                        <strong>{log.userEmail || log.userId || 'System'}</strong>
                        {log.userEmail && (
                          <span className="user-credits">
                            {(() => {
                              if (log.metadata?.userCreditsAfter !== undefined) {
                                return `${log.metadata.userCreditsAfter} credits`;
                              } else if (log.metadata?.after !== undefined) {
                                return `${log.metadata.after} credits`;
                              } else if (log.metadata?.userCreditsBefore !== undefined) {
                                return `${log.metadata.userCreditsBefore} credits`;
                              } else {
                                return 'User';
                              }
                            })()}
                          </span>
                        )}
                      </div>
                      <div className="card-message">
                        {log.isGrouped ? (
                          <div className="grouped-message">
                            <div className="request-summary">
                              <span className="message-text">{log.message}</span>
                              <span className="duration">{Math.round(log.duration || 0)}ms</span>
                              <span className={`status-indicator status-${log.status}`}>{log.status}</span>
                            </div>
                          </div>
                        ) : (
                          <>
                            {highlightMessage(log)}
                          </>
                        )}
                      </div>
                      <div className="card-source">
                        {log.sourceFile && (
                          <span className="source-tag">{log.sourceFile.split('/').pop()}</span>
                        )}
                        {log.requestId && (
                          <span className="request-tag">{log.requestId.substring(0, 8)}...</span>
                        )}
                      </div>
                    </div>
                    <div className="card-actions">
                      {log.isGrouped ? (
                        <button
                          onClick={() => openRequestTimeline(log)}
                          className="btn btn-primary btn-sm"
                        >
                          View Request Timeline
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => setSelectedLog(log)}
                            className="btn btn-secondary btn-sm"
                          >
                            View Details
                          </button>
                          {log.requestId && (
                            <button
                              onClick={() => openTimeline(log.requestId)}
                              className="btn btn-primary btn-sm"
                            >
                              Timeline
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.total > 0 && (
              <div className="pagination modern">
                <div className="pagination-info">
                  Showing <strong>{filters.offset + 1}</strong> to <strong>{Math.min(filters.offset + filters.limit, pagination.total)}</strong> of <strong>{formatNumber(pagination.total)}</strong> {groupLogs ? 'entries' : 'logs'}
                  {groupLogs && (
                    <span className="grouping-note"> (grouped by request)</span>
                  )}
                </div>
                <div className="pagination-controls">
            <button
              onClick={() => handleFilterChange('offset', Math.max(0, filters.offset - filters.limit))}
                    disabled={filters.offset === 0 || loading}
              className="btn btn-secondary btn-sm"
            >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M15 18l-6-6 6-6"></path>
                    </svg>
              Previous
            </button>
                  <span className="page-indicator">
                    Page {Math.floor(filters.offset / filters.limit) + 1} of {Math.ceil(pagination.total / filters.limit)}
            </span>
            <button
              onClick={() => handleFilterChange('offset', filters.offset + filters.limit)}
                    disabled={!pagination.hasMore || loading}
              className="btn btn-secondary btn-sm"
            >
              Next
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 18l6-6-6-6"></path>
                    </svg>
            </button>
                </div>
          </div>
            )}
          </>
        )}
      </div>

      {/* Enhanced Log Detail Modal */}
      {selectedLog && (
        <div className="modal-overlay modern" onClick={() => setSelectedLog(null)}>
          <div className="modal-content log-detail-modal modern" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header modern">
              <div className="modal-title-section">
                <div className="log-level-indicator" style={{
                  backgroundColor: `${getLogLevelColor(selectedLog.level)}20`,
                  borderColor: getLogLevelColor(selectedLog.level),
                  color: getLogLevelColor(selectedLog.level)
                }}>
                  {getLogLevelIcon(selectedLog.level)}
                  <span className="level-text">{selectedLog.level.toUpperCase()}</span>
                </div>
              <h3>Log Details</h3>
                {isHubSpotLog(selectedLog) && (
                  <span className="hubspot-badge">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="7" height="7"></rect>
                      <rect x="14" y="3" width="7" height="7"></rect>
                      <rect x="14" y="14" width="7" height="7"></rect>
                      <rect x="3" y="14" width="7" height="7"></rect>
                    </svg>
                    HubSpot
                  </span>
                )}
              </div>
              <div className="modal-actions">
                {selectedLog.requestId && (
                  <button
                    onClick={() => {
                      setSelectedLog(null);
                      openTimeline(selectedLog.requestId);
                    }}
                    className="btn btn-secondary btn-sm"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    View Timeline
                  </button>
                )}
                <button onClick={() => setSelectedLog(null)} className="modal-close modern">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
                </div>

            <div className="modal-body modern">
              {/* Key Information Cards */}
              <div className="detail-cards-grid">
                <div className="detail-card">
                  <div className="card-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                </div>
                  <div className="card-content">
                    <label>Timestamp</label>
                    <span className="value">{formatLogDate(selectedLog.timestamp)}</span>
                  </div>
                </div>

                <div className="detail-card">
                  <div className="card-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                  </div>
                  <div className="card-content">
                    <label>User</label>
                    <span className="value">{selectedLog.userEmail || selectedLog.userId || 'System'}</span>
                  </div>
                </div>

                <div className="detail-card">
                  <div className="card-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                    </svg>
                  </div>
                  <div className="card-content">
                    <label>Request ID</label>
                    <code className="value">{selectedLog.requestId || 'N/A'}</code>
                  </div>
                </div>

                <div className="detail-card">
                  <div className="card-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
                    </svg>
                  </div>
                  <div className="card-content">
                    <label>Source</label>
                    <span className="value">
                      {selectedLog.sourceFile || 'Unknown'}
                      {selectedLog.sourceFunction && (
                        <span className="source-function"> → {selectedLog.sourceFunction}</span>
                      )}
                  </span>
                </div>
                </div>

                <div className="detail-card">
                  <div className="card-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="9" y1="9" x2="15" y2="15"></line>
                      <line x1="15" y1="9" x2="9" y2="15"></line>
                    </svg>
                </div>
                  <div className="card-content">
                    <label>ID</label>
                    <code className="value selectable">{selectedLog.id}</code>
                  </div>
                </div>

                {selectedLog.ipAddress && (
                  <div className="detail-card">
                    <div className="card-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 2l3 3-3 3-3-3 3-3z"></path>
                        <path d="M12 8v8"></path>
                        <path d="M8 12h8"></path>
                      </svg>
                    </div>
                    <div className="card-content">
                      <label>IP Address</label>
                      <code className="value">{selectedLog.ipAddress}</code>
                    </div>
                  </div>
                )}
              </div>

              {/* Message Section */}
              <div className="message-section">
                <div className="section-header">
                  <h4>Message</h4>
                  <div className="message-badges">
                    {selectedLog.metadata?.statusCode && (
                      <span className="status-badge" style={{
                        backgroundColor: `${getStatusColor(selectedLog.metadata.statusCode)}20`,
                        color: getStatusColor(selectedLog.metadata.statusCode)
                      }}>
                        HTTP {selectedLog.metadata.statusCode}
                  </span>
                    )}
                    {selectedLog.metadata?.responseTime && (
                      <span className="response-time-badge">
                        {selectedLog.metadata.responseTime}ms
                      </span>
                    )}
                </div>
                </div>
                <div className="message-content">
                  {highlightMessage(selectedLog)}
                </div>
              </div>

              {/* Metadata Section */}
                {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                <div className="metadata-section">
                  <div className="section-header">
                    <h4>Metadata</h4>
                    <button
                      onClick={() => toggleMetadata(selectedLog.id)}
                      className="btn btn-secondary btn-sm"
                    >
                      {expandedMetadata[selectedLog.id] ? 'Collapse' : 'Expand'} JSON
                    </button>
                  </div>
                  {expandedMetadata[selectedLog.id] ? (
                    <pre className="metadata-json expanded">{JSON.stringify(selectedLog.metadata, null, 2)}</pre>
                  ) : (
                    <div className="metadata-grid">
                      {Object.entries(selectedLog.metadata).map(([key, value]) => (
                        <div key={key} className="metadata-item">
                          <strong>{key}:</strong>
                          <span>{typeof value === 'object' ? '[Object]' : String(value)}</span>
                        </div>
                      ))}
                  </div>
                )}
                  </div>
                )}

              {/* Stack Trace Section */}
              {selectedLog.stackTrace && (
                <div className="stack-trace-section">
                  <div className="section-header">
                    <h4>Stack Trace</h4>
                  </div>
                  <pre className="stack-trace-content">{selectedLog.stackTrace}</pre>
                  </div>
                )}

              {/* Additional Details Section */}
              <div className="additional-details-section">
                <div className="section-header">
                  <h4>Additional Details</h4>
                </div>
                <div className="details-grid">
                  {selectedLog.userAgent && (
                    <div className="detail-item">
                      <strong>User Agent:</strong>
                      <code className="detail-value">{selectedLog.userAgent}</code>
                    </div>
                  )}
                  {selectedLog.ipAddress && (
                    <div className="detail-item">
                      <strong>IP Address:</strong>
                      <span className="detail-value">{selectedLog.ipAddress}</span>
                    </div>
                  )}
                  {selectedLog.requestId && (
                    <div className="detail-item">
                      <strong>Request ID:</strong>
                      <code className="detail-value">{selectedLog.requestId}</code>
                    </div>
                  )}
                  {selectedLog.sourceFile && (
                    <div className="detail-item">
                      <strong>Source File:</strong>
                      <span className="detail-value">{selectedLog.sourceFile}</span>
                    </div>
                  )}
                  {selectedLog.sourceFunction && (
                    <div className="detail-item">
                      <strong>Source Function:</strong>
                      <span className="detail-value">{selectedLog.sourceFunction}</span>
                    </div>
                  )}
                  {selectedLog.userId && (
                    <div className="detail-item">
                      <strong>User ID:</strong>
                      <span className="detail-value">{selectedLog.userId}</span>
                    </div>
                  )}
                  {selectedLog.userEmail && (
                    <div className="detail-item">
                      <strong>User Email:</strong>
                      <span className="detail-value">{selectedLog.userEmail}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Enhanced Timeline Modal */}
      {timeline.open && (
        <div className="modal-overlay modern" onClick={closeTimeline}>
          <div className="modal-content timeline-modal modern" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header modern">
              <div className="modal-title-section">
                <div className="timeline-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                </div>
              <h3>Request Timeline</h3>
                <div className="request-id-display">
                  <code className="request-id">{timeline.requestId}</code>
                </div>
              </div>
              <div className="modal-actions">
                <button onClick={closeTimeline} className="modal-close modern">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
              </div>

            <div className="modal-body modern">
              {timeline.loading && (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading timeline...</p>
                </div>
              )}

              {timeline.error && (
                <div className="alert alert-error">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <span>{timeline.error}</span>
                </div>
              )}

              {!timeline.loading && !timeline.error && (
                <>
                  {/* Timeline Summary */}
                  <div className="timeline-summary">
                    <div className="summary-stats">
                      <div className="stat-item">
                        <span className="stat-value">{timeline.items.length}</span>
                        <span className="stat-label">Total Events</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-value">
                          {Math.round(new Date(timeline.items[timeline.items.length - 1]?.timestamp) - new Date(timeline.items[0]?.timestamp))}ms
                        </span>
                        <span className="stat-label">Duration</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-value error">
                          {timeline.items.filter(item => item.level === 'error').length}
                        </span>
                        <span className="stat-label">Errors</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-value warning">
                          {timeline.items.filter(item => item.level === 'warn').length}
                        </span>
                        <span className="stat-label">Warnings</span>
                      </div>
                    </div>
                  </div>

                  {/* Timeline Visualization */}
                  <div className="timeline-container">
                  {timeline.items.map((item, idx) => (
                      <div key={item.id || idx} className={`timeline-event log-${item.level} ${isHubSpotLog(item) ? 'hubspot-log' : ''}`}>
                        {/* Timeline connector line */}
                        <div className="timeline-connector">
                          <div className="timeline-dot" style={{
                            backgroundColor: getLogLevelColor(item.level),
                            borderColor: getLogLevelColor(item.level)
                          }}>
                            {getLogLevelIcon(item.level)}
                          </div>
                          {idx < timeline.items.length - 1 && <div className="timeline-line"></div>}
                        </div>

                        {/* Event content */}
                      <div className="timeline-content">
                          <div className="event-header">
                            <div className="event-time">
                              {formatLogDate(item.timestamp)}
                            </div>
                            <div className="event-badges">
                              <span className="level-badge" style={{
                                backgroundColor: `${getLogLevelColor(item.level)}20`,
                                borderColor: getLogLevelColor(item.level),
                                color: getLogLevelColor(item.level)
                              }}>
                                {item.level.toUpperCase()}
                              </span>

                          {item.metadata?.statusCode && (
                                <span className="status-badge" style={{
                                  backgroundColor: `${getStatusColor(item.metadata.statusCode)}20`,
                                  color: getStatusColor(item.metadata.statusCode)
                                }}>
                                  HTTP {item.metadata.statusCode}
                                  {item.metadata.responseTime && ` · ${item.metadata.responseTime}ms`}
                            </span>
                          )}

                              {isHubSpotLog(item) && (
                                <span className="hubspot-badge">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="3" width="7" height="7"></rect>
                                    <rect x="14" y="3" width="7" height="7"></rect>
                                    <rect x="14" y="14" width="7" height="7"></rect>
                                    <rect x="3" y="14" width="7" height="7"></rect>
                                  </svg>
                                  HubSpot
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="event-body">
                            <div className="event-message">
                              {highlightMessage(item)}
                            </div>

                          {item.metadata?.method && item.metadata?.url && (
                              <div className="event-route">
                                <span className="route-method">{item.metadata.method}</span>
                                <span className="route-url">{item.metadata.url}</span>
                              </div>
                            )}

                            {/* All Available Data */}
                            <details className="event-metadata">
                              <summary className="metadata-toggle">
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M12 5v14"></path>
                                  <path d="M5 12h14"></path>
                                </svg>
                                All Event Data
                              </summary>
                              <div className="metadata-content">
                                <div className="event-details-grid">
                                  {item.userAgent && (
                                    <div className="event-detail-item">
                                      <strong>User Agent:</strong>
                                      <code>{item.userAgent}</code>
                                    </div>
                                  )}
                                  {item.ipAddress && (
                                    <div className="event-detail-item">
                                      <strong>IP Address:</strong>
                                      <span>{item.ipAddress}</span>
                                    </div>
                                  )}
                                  {item.requestId && (
                                    <div className="event-detail-item">
                                      <strong>Request ID:</strong>
                                      <code>{item.requestId}</code>
                                    </div>
                                  )}
                                  {item.sourceFile && (
                                    <div className="event-detail-item">
                                      <strong>Source File:</strong>
                                      <span>{item.sourceFile}</span>
                                    </div>
                                  )}
                                  {item.sourceFunction && (
                                    <div className="event-detail-item">
                                      <strong>Source Function:</strong>
                                      <span>{item.sourceFunction}</span>
                                    </div>
                                  )}
                                  {item.userId && (
                                    <div className="event-detail-item">
                                      <strong>User ID:</strong>
                                      <span>{item.userId}</span>
                                    </div>
                                  )}
                                  {item.userEmail && (
                                    <div className="event-detail-item">
                                      <strong>User Email:</strong>
                                      <span>{item.userEmail}</span>
                                    </div>
                                  )}
                                </div>
                                {item.metadata && Object.keys(item.metadata).length > 0 && (
                                  <div className="metadata-json-section">
                                    <h5>Metadata JSON:</h5>
                                    <pre className="metadata-json">{JSON.stringify(item.metadata, null, 2)}</pre>
                                  </div>
                                )}
                              </div>
                            </details>

                            {/* Stack trace if present */}
                        {item.stackTrace && (
                              <details className="event-stack-trace">
                                <summary className="stack-trace-toggle">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
                                  </svg>
                                  Stack Trace
                                </summary>
                                <pre className="stack-trace-content">{item.stackTrace}</pre>
                          </details>
                        )}
                          </div>
                      </div>
                    </div>
                  ))}

                    {timeline.items.length === 0 && (
                      <div className="empty-timeline">
                        <div className="empty-icon">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <polyline points="12 6 12 12 16 14"></polyline>
                          </svg>
                        </div>
                        <p>No timeline events found</p>
                        <p className="empty-hint">This request may not have detailed logging enabled</p>
                </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
